import React, { useState, useEffect } from "react";
import { MdSchool } from "react-icons/md";
import MentionServiceInstance from "service/mention.service";
import HeaderSection from "components/headers-section";
import ActionBar from "components/action-bar";
import MentionTable from "./components/MentionTable";
import MentionFormModal from "./components/MentionFormModal";
import DeleteConfirmationModal from "components/delete-confirm-modal";

const MentionManager = () => {
  const [mentions, setMentions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentMention, setCurrentMention] = useState({
    name_mention: "",
    status_mention: true,
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchMentions();
  }, []);

  const fetchMentions = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await MentionServiceInstance.getAll();
      setMentions(result || []);
    } catch (error) {
      setError(
        "Impossible de charger les mentions. Veuillez réessayer plus tard."
      );
      console.error("Error fetching mentions:", error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setCurrentMention({
      name_mention: "",
      status_mention: true,
    });
    setIsEditing(false);
    setIsFormModalOpen(true);
  };

  const openEditModal = (mention) => {
    setCurrentMention({
      id_mention: mention.id_mention,
      name_mention: mention.name_mention,
      status_mention: mention.status_mention,
    });
    setIsEditing(true);
    setIsFormModalOpen(true);
  };

  const openDeleteModal = (mention) => {
    setCurrentMention(mention);
    setIsDeleteModalOpen(true);
  };

  const handleSubmitForm = async (formData) => {
    setLoading(true);
    try {
      if (isEditing) {
        await MentionServiceInstance.update(
          currentMention.id_mention,
          formData
        );
      } else {
        await MentionServiceInstance.create(formData);
      }

      fetchMentions();
      setIsFormModalOpen(false);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        errors: error.response?.data?.errors || {
          general: isEditing
            ? "Impossible de modifier la mention. Veuillez réessayer."
            : "Impossible de créer la mention. Veuillez réessayer.",
        },
      };
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await MentionServiceInstance.delete(currentMention.id_mention);
      fetchMentions();
      setIsDeleteModalOpen(false);
    } catch (error) {
      setError("Impossible de supprimer la mention. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const filteredMentions = mentions.filter((mention) =>
    mention.name_mention.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 px-5 py-8 md:px-8">
      <HeaderSection
        title="Gestion des Mentions"
        description="Créez et gérez les mentions pour vos formations"
        accentColor="#57A863"
      />

      <ActionBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Rechercher une mention..."
        searchIcon={MdSchool}
        onRefresh={fetchMentions}
        onCreateNew={openCreateModal}
        buttonLabel="Nouvelle Mention"
        loading={loading}
        primaryColor="#57A863"
        hoverColor="#4c9657"
      />

      {error && (
        <div
          className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600"
          role="alert"
        >
          {error}
        </div>
      )}

      <MentionTable
        mentions={filteredMentions}
        loading={loading}
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />  

      <MentionFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSubmit={handleSubmitForm}
        initialMention={currentMention}
        isEditing={isEditing}
        existingMentions={mentions}
        loading={loading}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        item={currentMention}
        itemName={currentMention.name_mention}
        itemType="mention"
        warning="La suppression de cette mention peut affecter les formations et les étudiants qui y sont associés."
        loading={loading}
      />
    </div>
  );
};

export default MentionManager;
